/**
 * format.ts — THE formatting engine. One money, one date, one duration, one label.
 *
 * The One Engine Rule: if a value is shown to a human, it is formatted HERE. Pages do
 * not roll their own — that is how the app ended up with 7 money formats, 5 date
 * dialects, 15 duration formatters and "Cold_call".
 *
 * ── MONEY ────────────────────────────────────────────────────────────────────
 * `formatMoney` is canonical. `formatCurrency` / `formatCurrencyCompact` are thin
 * back-compat shims over it (~40 existing positional call sites) — one implementation,
 * three doors. New code calls formatMoney.
 *
 * CURRENCY IS DATA, NOT A CONSTANT. Amounts render in the currency the record (or the
 * workspace) actually carries. EUR is the house currency and the default, but a genuine
 * non-EUR record must still render honestly — inventing € on a USD row would be a lie.
 * Locale follows the currency, so each reads natively: "1.234.567 €" vs "$1,234,567".
 *
 * ── DATES ────────────────────────────────────────────────────────────────────
 * ONE app-wide format, workspace-configurable since migration 0153: European
 * day-first `17/06/2026` by default; Settings → CRM → Currency & format can
 * switch the workspace to US or ISO ordering. The preference lives in the
 * `workspace_settings` row and is pushed here once per session by
 * ModuleContext via `setFormatPreferences` — this module stays a plain
 * synchronous library (it is called from non-React code), so it cannot read
 * the database itself. Until the migration is applied nothing calls the
 * setter and every date renders exactly as before.
 *
 * (Historical note: Settings → Localization once had a DEAD Date Format
 * dropdown — a useState that was never saved. The real, saved preference is
 * the 0153 workspace setting above; that dropdown's promise is now kept.)
 *
 * ── DURATIONS ────────────────────────────────────────────────────────────────
 * Long spans roll over into days. "43d 2h", never "1034h 58m".
 */

// ─── MONEY ────────────────────────────────────────────────────────────────────

/** Locale per currency so amounts read natively ($2,500,000 vs 2.500.000 €). */
const CURRENCY_LOCALE: Record<string, string> = {
  EUR: 'de-DE', USD: 'en-US', GBP: 'en-GB', CHF: 'de-CH', CAD: 'en-CA',
  AUD: 'en-AU', JPY: 'ja-JP', SEK: 'sv-SE', NOK: 'nb-NO', DKK: 'da-DK', PLN: 'pl-PL',
};

/** Short symbol for the compact formatter (falls back to the ISO code). */
const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF', CAD: 'C$', AUD: 'A$',
  JPY: '¥', SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł',
};

export interface MoneyOptions {
  /** ISO 4217 code. Defaults to EUR, the house currency. */
  currency?: string;
  /** Decimal places. 0 = whole units (default); pass 2 for invoices/expenses. */
  fractionDigits?: number;
  /**
   * Abbreviate large amounts for TIGHT spaces (a donut centre, a KPI tile):
   * 2_477_845 → "2,48 M €". Under 100k renders in full — it already fits.
   */
  compact?: boolean;
}

// ─── WORKSPACE FORMAT PREFERENCES (migration 0153) ───────────────────────────

/** The three date orderings the workspace can choose. */
export type DateFormatKey = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
/** The three digit-grouping styles, named by how 1234.56 renders. */
export type NumberFormatKey = '1.234,56' | '1,234.56' | '1 234,56';

/** Each grouping style is a real locale so Intl does the work. */
const NUMBER_FORMAT_LOCALE: Record<NumberFormatKey, string> = {
  '1.234,56': 'de-DE',
  '1,234.56': 'en-US',
  '1 234,56': 'fr-FR',
};

interface FormatPreferences {
  dateFormat: DateFormatKey;
  numberFormat: NumberFormatKey | null; // null = follow the currency's native locale
}

/** Session-level preferences. Defaults reproduce the pre-0153 behaviour exactly. */
const prefs: FormatPreferences = { dateFormat: 'DD/MM/YYYY', numberFormat: null };

/**
 * Push the workspace's saved formats into this engine. Called by ModuleContext
 * when the `workspace_settings` row loads (and again after the admin saves a
 * change). Passing undefined leaves that preference untouched.
 */
export function setFormatPreferences(next: {
  dateFormat?: DateFormatKey;
  numberFormat?: NumberFormatKey | null;
}): void {
  if (next.dateFormat !== undefined) prefs.dateFormat = next.dateFormat;
  if (next.numberFormat !== undefined) prefs.numberFormat = next.numberFormat;
}

const localeFor = (cur: string): string =>
  (prefs.numberFormat ? NUMBER_FORMAT_LOCALE[prefs.numberFormat] : undefined)
  ?? CURRENCY_LOCALE[cur]
  ?? 'en-US';

/**
 * THE money formatter. Every amount shown to a human goes through this.
 *
 *   formatMoney(1234567)                        → "1.234.567 €"
 *   formatMoney(1234.5,  { fractionDigits: 2 }) → "1.234,50 €"
 *   formatMoney(2477845, { compact: true })     → "2,48 M €"
 *   formatMoney(477, { currency: 'USD' })       → "$477"
 */
export function formatMoney(amount: number, opts: MoneyOptions = {}): string {
  const { currency, fractionDigits = 0, compact = false } = opts;
  const cur = currency || 'EUR';
  const n = amount || 0;

  if (compact) return compactMoney(n, cur);

  return new Intl.NumberFormat(localeFor(cur), {
    style: 'currency',
    currency: cur,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/**
 * Compact money. Keeps the locale's decimal mark (comma for EUR) and puts the symbol
 * as ONE trailing token, so it can never collide with the digits. Bounded to ~8 chars:
 *   0 → "0 €" · 1_200 → "1.200 €" · 2_477_845 → "2,48 M €" · 1_500_000_000 → "1,5 B €"
 */
function compactMoney(n: number, cur: string): string {
  const abs = Math.abs(n);
  if (abs < 100_000) return formatMoney(n, { currency: cur });

  const units: { v: number; s: string }[] = [
    { v: 1e12, s: 'T' }, { v: 1e9, s: 'B' }, { v: 1e6, s: 'M' }, { v: 1e3, s: 'K' },
  ];
  // More decimals for smaller mantissas so precision stays reasonable, fewer as it
  // grows — the string width stays bounded either way.
  const decimalsFor = (x: number): number =>
    Math.abs(x) >= 100 ? 0 : Math.abs(x) >= 10 ? 1 : 2;

  let idx = units.findIndex(x => abs >= x.v);
  if (idx < 0) idx = units.length - 1;
  let scaled = n / units[idx].v;
  // Rounding can lift the mantissa to 1000 (999,900 → "1.000 K"); carry it into the
  // next-larger unit so it reads "1 M". Never needed past the top unit.
  if (idx > 0 && Math.abs(Number(scaled.toFixed(decimalsFor(scaled)))) >= 1000) {
    idx -= 1;
    scaled = n / units[idx].v;
  }
  const num = new Intl.NumberFormat(localeFor(cur), {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalsFor(scaled),
  }).format(scaled);
  return `${num} ${units[idx].s} ${CURRENCY_SYMBOL[cur] ?? cur}`;
}

/**
 * Back-compat shim over {@link formatMoney} for the positional call sites.
 * Prefer formatMoney in new code.
 */
export function formatCurrency(
  amount: number,
  currency: string = 'EUR',
  fractionDigits: number = 0,
): string {
  return formatMoney(amount, { currency, fractionDigits });
}

/** Back-compat shim — same as `formatMoney(v, { compact: true })`. */
export function formatCurrencyCompact(amount: number, currency: string = 'EUR'): string {
  return formatMoney(amount, { currency, compact: true });
}

/**
 * Money that tolerates a missing amount, rendering the house em-dash instead of "€0".
 * A null value means "not set", which is NOT the same fact as zero — so it must not
 * format as a number.
 *
 * This is THE null-tolerant money wrapper. It replaced two identical private copies
 * (`money` in components/crm/RecordDetailPanel and a local `formatCurrency` in
 * pages/crm/DealsPage), both of which were already delegating to formatMoney with
 * 0 fraction digits. Output is unchanged from both.
 *
 *   formatMoneyOrDash(null)               → "—"
 *   formatMoneyOrDash(145000)             → "145.000 €"
 *   formatMoneyOrDash(500, 'USD')         → "$500"
 */
export function formatMoneyOrDash(
  value: number | null | undefined,
  currency?: string | null,
): string {
  // `== null` catches undefined too; 0 is a real amount and must still format.
  return value == null ? '—' : formatMoney(value, { currency: currency || 'EUR' });
}

// ─── DATES ────────────────────────────────────────────────────────────────────

/**
 * The date locale follows the workspace preference (see the header). Each key
 * maps to a locale whose native short-date ordering IS that format:
 * en-GB → 17/06/2026 · en-US → 06/17/2026 · sv-SE → 2026-06-17.
 */
const DATE_FORMAT_LOCALE: Record<DateFormatKey, string> = {
  'DD/MM/YYYY': 'en-GB',
  'MM/DD/YYYY': 'en-US',
  'YYYY-MM-DD': 'sv-SE',
};
const dateLocale = (): string => DATE_FORMAT_LOCALE[prefs.dateFormat];

/** What every date formatter shows when there is no date. Never render "Invalid Date". */
export const EMPTY_DATE = '—';

export interface DateOptions {
  /** Append the time: "17/06/2026, 14:30". */
  withTime?: boolean;
  /** What to show for null/undefined/unparseable input. Defaults to "—". */
  fallback?: string;
}

/** Parse anything date-ish; null when it isn't a real date (never NaN downstream). */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * THE date formatter.
 *
 *   formatDate('2026-06-17T14:30:00Z')                    → "17/06/2026"
 *   formatDate('2026-06-17T14:30:00Z', { withTime: true }) → "17/06/2026, 14:30"
 *   formatDate(null)                                       → "—"
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  opts: DateOptions = {},
): string {
  const { withTime = false, fallback = EMPTY_DATE } = opts;
  const d = toDate(value);
  if (!d) return fallback;

  return d.toLocaleString(dateLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  });
}

/** Convenience: date + time. Same engine, same rule. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback: string = EMPTY_DATE,
): string {
  return formatDate(value, { withTime: true, fallback });
}

/** Time only, 24h — "14:30". For chat/attendance rows where the day is already known. */
export function formatTime(
  value: string | number | Date | null | undefined,
  fallback: string = EMPTY_DATE,
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * THE relative date formatter — day-granularity, future and past.
 *
 * Moved here from components/crm/views/fieldRenderers so that ONE module owns date
 * display. It had been living next to its only caller, which is how the app ended up with
 * three date conventions in the CRM at once: relative here, absolute `formatDate` in the
 * pages, and a hand-rolled "14d ago" on DealsPage — with two of them sometimes in the
 * SAME table.
 *
 * Works in LOCAL calendar days, so "today" doesn't flip on the wall clock. Near dates read
 * in days, then months, then years — an old created_at shows "8 months ago" rather than a
 * meaningless "243 days ago". Returns '' for an unparseable value.
 *
 * THE DISPLAY RULE: relative text is what you SHOW; the absolute date belongs in a `title`
 * so it is one hover away. "8 months ago" answers "is this stale?" at a glance, which is
 * what a list is for, but it cannot answer "which day exactly?" — so never use it alone
 * where the precise date is the point.
 *
 *   formatRelativeDate('2026-07-19T…')  → "yesterday"
 *   formatRelativeDate('2025-11-19T…')  → "8 months ago"
 *   formatRelativeDate('2026-08-03T…')  → "in 14 days"
 */
/**
 * "today" / "yesterday" / "Nd ago" — CALENDAR-DAY boundaries, compact vocabulary.
 *
 * Exists because the two available options were each wrong in one direction. The old
 * component-local `daysAgo` floored ELAPSED MILLISECONDS, so a timestamp 21h old that
 * crossed midnight read "today" when it was yesterday. `formatRelativeDate` fixes the
 * boundary but also coarsens: "45d ago" becomes "2 months ago", "400d ago" becomes
 * "1 year ago". That is a loss of precision on a freshness indicator, and was not the
 * change being asked for.
 *
 * So: same day extraction as formatRelativeDate (they cannot disagree about which day a
 * timestamp falls on), the terse unit vocabulary kept. Future dates fall through to
 * "today" — this labels staleness, not schedule; use formatRelativeDate for dates ahead.
 */
export function formatDaysAgo(value: string | null | undefined): string {
  if (!value) return '';
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const d = Math.round((startOfToday - startOfThen) / 86400000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d}d ago`;
}

export function formatRelativeDate(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const d = Math.round((startOfThen - startOfToday) / 86400000);

  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';

  const abs = Math.abs(d);
  const future = d > 0;
  let value: number;
  let unit: string;
  if (abs < 30) { value = abs; unit = 'day'; }
  else if (abs < 365) { value = Math.round(abs / 30); unit = 'month'; }
  else { value = Math.round(abs / 365); unit = 'year'; }
  const label = `${value} ${unit}${value === 1 ? '' : 's'}`;
  return future ? `in ${label}` : `${label} ago`;
}

/**
 * True when an ISO date's LOCAL calendar day is strictly before today — the "overdue" test
 * a caller pairs with an open-record check (e.g. a past close_date on an open deal).
 * A date falling TODAY is NOT past (due today ≠ overdue). Shares formatRelativeDate's
 * day-level extraction, so a cell can never say "today" while also being flagged overdue.
 * Unparseable/empty → false.
 */
export function isPastDate(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return false;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  return startOfThen < startOfToday;
}

// ─── DURATIONS ────────────────────────────────────────────────────────────────

export interface DurationOptions {
  /** Show at most this many units. 2 = "43d 2h" (the default), 1 = "43d". */
  maxUnits?: number;
}

/**
 * THE duration formatter — a span of milliseconds as a short human string.
 *
 * ROLLS OVER INTO DAYS. This is the whole point: hours must not accumulate unbounded,
 * or a 43-day-old SLA breach reads "1034h 58m" instead of "43d 2h".
 *
 *   formatDuration(90_000)      → "1m"
 *   formatDuration(5_400_000)   → "1h 30m"
 *   formatDuration(3_723_000_0) → "10h 20m"
 *   formatDuration(3_722_280_000) → "43d 2h"
 *
 * Sub-minute spans read "0m" rather than "0s" — every caller shows minute-or-coarser.
 */
export function formatDuration(ms: number, opts: DurationOptions = {}): string {
  const { maxUnits = 2 } = opts;
  const total = Math.abs(Math.trunc(ms)) || 0;

  // Round to whole minutes FIRST, then decompose — so 59.7m becomes 1h 0m, never the
  // "1h 60m" the old per-unit Math.round could produce.
  const totalMinutes = Math.round(total / 60_000);
  const d = Math.floor(totalMinutes / 1440);
  const h = Math.floor((totalMinutes % 1440) / 60);
  const m = totalMinutes % 60;

  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (parts.length === 0) return '0m';

  return parts.slice(0, Math.max(1, maxUnits)).join(' ');
}

/**
 * A past instant as "just now" / "5m ago" / "2h ago" / "43d ago".
 * Built on {@link formatDuration}, so it inherits the day rollover.
 */
export function formatRelative(
  value: string | number | Date | null | undefined,
  fallback: string = EMPTY_DATE,
): string {
  const d = toDate(value);
  if (!d) return fallback;
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  return `${formatDuration(diff, { maxUnits: 1 })} ago`;
}

// ─── ENUM LABELS ──────────────────────────────────────────────────────────────

/**
 * THE enum humanizer: a stored key → a label a human can read.
 *
 *   humanizeLabel('cold_call')      → "Cold Call"
 *   humanizeLabel('EMAIL_CAMPAIGN') → "Email Campaign"
 *   humanizeLabel('in-progress')    → "In Progress"
 *   humanizeLabel(null)             → ""
 *
 * Splits on underscores, hyphens AND whitespace — the bug this replaces
 * (`key.charAt(0).toUpperCase() + key.slice(1)`) only touched index 0, so the
 * separator survived and "cold_call" rendered as "Cold_call".
 *
 * For enums with a designed label ("KYC", "MRR", "Closed Won"), prefer an explicit
 * options map — this is the fallback for keys with no curated label.
 */
export function humanizeLabel(value: string | null | undefined): string {
  if (!value) return '';
  return String(value)
    .trim()
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
